// src/pages/Home.ts

import "./Home.css";
import {searchMap} from "../searchMap";
import {useTheme} from "../context/useTheme";
import {useAuth} from "../context/useAuth";
import {renderHomeSearchSuggestions} from "../components/HomeSearchSuggestions";
import {renderProfileDropdown} from "../components/ProfileDropdown";

type HomeLanguage = "yo" | "ha" | "ig" | "en";

interface LocalizedText {
  yo: string;
  ha: string;
  ig: string;
  en: string;
}

interface HomeTopic {
  id: string;
  label: LocalizedText;
  icon: string;
}

interface DiscoverSource {
  name: string;
  domain: string;
}

interface DiscoverTopic {
  name: string;
  slug: string;
}

interface HomeNewsArticle {
  id: string;
  title: string;
  summary: string;
  image_url: string;
  thumbnail_url: string;
  image_focal_x: number | null;
  image_focal_y: number | null;
  article_url: string;
  published_at: string;
  is_featured: boolean;
  source: DiscoverSource;
  topics: DiscoverTopic[];
  primary_topic: DiscoverTopic | null;
}

interface DiscoverFeedResponse {
  next: string | null;
  previous: string | null;
  results: HomeNewsArticle[];
  feed_request_id: string;
}

const HOME_LANGUAGE_STORAGE_KEY = "ayesearch-home-language";

const HOME_LANGUAGES: Array<{
  id: HomeLanguage;
  name: string;
  shortName: string;
}> = [
  {id: "yo", name: "Yorùbá", shortName: "YOR"},
  {id: "ha", name: "Hausa", shortName: "HAU"},
  {id: "ig", name: "Igbo", shortName: "IGB"},
  {id: "en", name: "English", shortName: "ENG"},
];

let activeHomeLanguage: HomeLanguage = readStoredHomeLanguage();
let activeHomeTopic = "top";

function readStoredHomeLanguage(): HomeLanguage {
  try {
    const storedLanguage = window.localStorage.getItem(
      HOME_LANGUAGE_STORAGE_KEY,
    );

    if (
      storedLanguage === "yo" ||
      storedLanguage === "ha" ||
      storedLanguage === "ig" ||
      storedLanguage === "en"
    ) {
      return storedLanguage;
    }
  } catch {
    // localStorage can be unavailable in private/restricted browser contexts.
  }

  return "yo";
}

function saveHomeLanguage(language: HomeLanguage): void {
  activeHomeLanguage = language;

  try {
    window.localStorage.setItem(HOME_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Keep the in-memory selection when localStorage is unavailable.
  }
}

function textFor(value: LocalizedText): string {
  return value[activeHomeLanguage] || value.en;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function normalizeQuery(q: string): string {
  return q
    .toLowerCase()
    .trim()
    .replace(/\.(jpg|jpeg|png|gif|webp)$/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getProfileColor(email: string): string {
  const colors = ["#4285f4", "#ea4335", "#fbbc05", "#34a853", "#a142f4"];
  let hash = 0;

  for (let i = 0; i < email.length; i += 1) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

function getThemeLabel(theme: string): string {
  switch (theme) {
    case "device":
      return textFor(UI_TEXT.device);
    case "white":
      return textFor(UI_TEXT.white);
    case "light":
      return textFor(UI_TEXT.silver);
    case "dark":
      return textFor(UI_TEXT.dark);
    default:
      return textFor(UI_TEXT.device);
  }
}

const UI_TEXT = {
  about: {
    yo: "Nípa",
    ha: "Game da",
    ig: "Banyere",
    en: "About",
  },
  store: {
    yo: "Ilé ìtajà",
    ha: "Shago",
    ig: "Ụlọ ahịa",
    en: "Store",
  },
  theme: {
    yo: "Àkòrí",
    ha: "Jigo",
    ig: "Isiokwu",
    en: "Theme",
  },
  device: {
    yo: "Ẹrọ",
    ha: "Na'ura",
    ig: "Ngwaọrụ",
    en: "Device",
  },
  white: {
    yo: "Funfun",
    ha: "Fari",
    ig: "Ọcha",
    en: "White",
  },
  silver: {
    yo: "Fàdákà",
    ha: "Azurfa",
    ig: "Ọlaọcha",
    en: "Silver",
  },
  dark: {
    yo: "Òkùnkùn",
    ha: "Duhu",
    ig: "Ọchịchịrị",
    en: "Dark",
  },
  images: {
    yo: "Àwòrán",
    ha: "Hotuna",
    ig: "Foto",
    en: "Images",
  },
  signIn: {
    yo: "Wọlé",
    ha: "Shiga",
    ig: "Banye",
    en: "Sign In",
  },
  all: {
    yo: "Gbogbo",
    ha: "Duka",
    ig: "Niile",
    en: "All",
  },
  openApps: {
    yo: "Ṣí àwọn app",
    ha: "Buɗe apps",
    ig: "Mepee ngwa",
    en: "Open apps",
  },
  openNavigation: {
    yo: "Ṣí àkójọ",
    ha: "Buɗe kewayawa",
    ig: "Mepee nsogharị",
    en: "Open navigation",
  },
  discover: {
    yo: "Ṣàwárí",
    ha: "Gano",
    ig: "Chọpụta",
    en: "Discover",
  },
  newsForYou: {
    yo: "Ìròyìn fún ọ",
    ha: "Labarai domin ku",
    ig: "Akụkọ maka gị",
    en: "News for you",
  },
  moreNews: {
    yo: "Ìròyìn síi",
    ha: "Ƙarin labarai",
    ig: "Akụkọ ndị ọzọ",
    en: "More news",
  },
  topStory: {
    yo: "Ìròyìn pàtàkì",
    ha: "Babban labari",
    ig: "Akụkọ kacha mkpa",
    en: "Top story",
  },
  chooseLanguage: {
    yo: "Yan èdè",
    ha: "Zaɓi harshe",
    ig: "Họrọ asụsụ",
    en: "Choose language",
  },
  closeLanguage: {
    yo: "Pa yíyan èdè",
    ha: "Rufe zaɓin harshe",
    ig: "Mechie nhọrọ asụsụ",
    en: "Close language picker",
  },
  searchPlaceholder: {
    yo: "Ṣàwárí lórí ayélujára...",
    ha: "Bincika yanar gizo...",
    ig: "Chọọ na webụ...",
    en: "Search the web...",
  },
  searchButton: {
    yo: "AyeSearch",
    ha: "AyeSearch",
    ig: "AyeSearch",
    en: "AyeSearch",
  },
} satisfies Record<string, LocalizedText>;

const APPS_LIST = [
  {
    name: "VibeJoos",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/Vibejoo_Icon.png",
  },
  {
    name: "AMailMe",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/Amailme_Icon.png",
  },
  {
    name: "OreChat",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/OreChat_Icon.png",
  },
  {
    name: "ADrive",
    icon: "☁",
    color: "#ffd43b",
    image: "",
  },
  {
    name: "ADocs",
    icon: "📄",
    color: "#845ef7",
    image: "",
  },
  {
    name: "ANote",
    icon: "📝",
    color: "#f06595",
    image: "",
  },
];

const HOME_TOPICS: HomeTopic[] = [
  {
    id: "top",
    label: {yo: "Ìròyìn pàtàkì", ha: "Manyan labarai", ig: "Akụkọ kachasị", en: "Top stories"},
    icon: "🔥",
  },
  {
    id: "wiki",
    label: {yo: "Aikipedia", ha: "Aikipedia", ig: "Aikipedia", en: "Aikipedia"},
    icon: "📚",
  },
  {
    id: "games",
    label: {yo: "Eré", ha: "Wasanni", ig: "Egwuregwu", en: "Games"},
    icon: "🎮",
  },
  {
    id: "anime",
    label: {yo: "Anime", ha: "Anime", ig: "Anime", en: "Anime"},
    icon: "✨",
  },
  {
    id: "technology",
    label: {yo: "Ìmọ̀ ẹ̀rọ", ha: "Fasaha", ig: "Teknụzụ", en: "Technology"},
    icon: "💻",
  },
  {
    id: "sports",
    label: {yo: "Eré ìdárayá", ha: "Wasanni", ig: "Egwuregwu", en: "Sports"},
    icon: "🏀",
  },
  {
    id: "business",
    label: {yo: "Iṣòwò", ha: "Kasuwanci", ig: "Azụmahịa", en: "Business"},
    icon: "📈",
  },
  {
    id: "entertainment",
    label: {yo: "Eré ìnàjú", ha: "Nishaɗi", ig: "Ntụrụndụ", en: "Entertainment"},
    icon: "🎬",
  },
];

const DISCOVER_API_URL = "https://api.ayesearch.com/api/discover/feed/";
const FALLBACK_NEWS_IMAGE = "/static/ayesearch/Ayesearch_Icon.png";

let homeNewsArticles: HomeNewsArticle[] = [];
let homeNewsLoading = true;
let homeNewsError = "";
let homeFeedRequestId = "";
let homeFeedAbortController: AbortController | null = null;

function discoverTopicFor(category: string): string {
  return category === "top" ? "" : category;
}

function buildDiscoverUrl(
  category: string,
  language: HomeLanguage = activeHomeLanguage,
): string {
  const url = new URL(DISCOVER_API_URL);
  url.searchParams.set("country", "NG");
  url.searchParams.set("language", language);
  url.searchParams.set("limit", "10");

  const topic = discoverTopicFor(category);
  if (topic) url.searchParams.set("topic", topic);

  return url.toString();
}

function relativePublishedTime(value: string): string {
  const published = new Date(value).getTime();
  if (!Number.isFinite(published)) return "";

  const seconds = Math.max(0, Math.floor((Date.now() - published) / 1000));
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(published));
}

function articleImage(article: HomeNewsArticle): string {
  return article.thumbnail_url || article.image_url || FALLBACK_NEWS_IMAGE;
}

function imageObjectPosition(article: HomeNewsArticle): string {
  const clamp = (value: number | null): number => {
    if (typeof value !== "number" || !Number.isFinite(value)) return 0.5;
    return Math.min(1, Math.max(0, value));
  };

  return `${clamp(article.image_focal_x) * 100}% ${
    clamp(article.image_focal_y) * 100
  }%`;
}

async function loadDiscoverFeed(category = activeHomeTopic): Promise<void> {
  homeFeedAbortController?.abort();
  const controller = new AbortController();
  homeFeedAbortController = controller;
  homeNewsLoading = true;
  homeNewsError = "";

  const results = document.getElementById("home-news-results");
  if (results) results.innerHTML = generateNewsCardsHtml();

  try {
    const response = await fetch(buildDiscoverUrl(category), {
      method: "GET",
      headers: {Accept: "application/json"},
      signal: controller.signal,
      credentials: "omit",
    });

    if (!response.ok) throw new Error(`Discover API returned ${response.status}`);
    let payload = (await response.json()) as DiscoverFeedResponse;
    if (!Array.isArray(payload.results)) throw new Error("Invalid Discover response");

    // Yoruba, Hausa and Igbo articles will appear as those pipelines grow.
    // Until then, keep the home feed useful by falling back to English.
    if (payload.results.length === 0 && activeHomeLanguage !== "en") {
      const fallbackResponse = await fetch(buildDiscoverUrl(category, "en"), {
        method: "GET",
        headers: {Accept: "application/json"},
        signal: controller.signal,
        credentials: "omit",
      });
      if (fallbackResponse.ok) {
        const fallbackPayload = (await fallbackResponse.json()) as DiscoverFeedResponse;
        if (Array.isArray(fallbackPayload.results)) payload = fallbackPayload;
      }
    }

    homeNewsArticles = payload.results;
    homeFeedRequestId = payload.feed_request_id || "";
  } catch (error) {
    if (controller.signal.aborted) return;
    homeNewsArticles = [];
    homeNewsError = error instanceof Error ? error.message : "Could not load stories.";
  } finally {
    if (controller.signal.aborted) return;
    homeNewsLoading = false;
    const currentResults = document.getElementById("home-news-results");
    if (currentResults) currentResults.innerHTML = generateNewsCardsHtml();
  }
}

function generateAppsMenuHtml(): string {
  return `
    <div class="apps-menu hidden" id="apps-dropdown-panel">
      <div class="apps-menu-grid">
        ${APPS_LIST.map(
          (app) => `
            <div class="app-item">
              ${
                app.image
                  ? `<img
                      class="app-icon app-icon-image"
                      src="${app.image}"
                      alt="${escapeHtml(app.name)}"
                    />`
                  : `<div
                      class="app-icon"
                      style="background-color: ${app.color}"
                      aria-hidden="true"
                    >${app.icon}</div>`
              }
              <span>${escapeHtml(app.name)}</span>
            </div>
          `,
        ).join("")}
      </div>
      <div class="apps-download-hub">
        <div class="apps-download-hub-header">
          <img
            class="apps-browser-icon"
            src="/static/ayesearch/Ayesearch_Icon.png"
            alt="AyeSearch"
          />
          <div class="apps-browser-copy">
            <strong>Get AyeSearch</strong>
            <span>Choose mobile or desktop browser.</span>
          </div>
        </div>

        <div class="apps-download-platforms">
          <div class="apps-download-platform">
            <span class="apps-download-kicker">Mobile app</span>
            <strong>iOS / Android</strong>
            <a
              class="apps-browser-download-btn"
              href="https://ayesearch.com/downloads/"
              aria-label="Download AyeSearch for iOS or Android"
            >
              Download
            </a>
          </div>

          <div class="apps-download-platform">
            <span class="apps-download-kicker">AyeSearch Browser</span>
            <strong>Mac / Windows</strong>
            <a
              class="apps-browser-download-btn"
              href="https://ayesearch.com/downloads/"
              aria-label="Download AyeSearch Browser for Mac or Windows"
            >
              Download
            </a>
          </div>
        </div>
      </div>
    </div>
  `;
}

function generateTopicTabsHtml(): string {
  return HOME_TOPICS.map(
    (topic) => `
      <button
        class="home-topic-chip ${topic.id === activeHomeTopic ? "active" : ""}"
        type="button"
        data-topic="${topic.id}"
        aria-pressed="${topic.id === activeHomeTopic ? "true" : "false"}"
      >
        <span class="home-topic-icon" aria-hidden="true">${topic.icon}</span>
        <span>${escapeHtml(textFor(topic.label))}</span>
      </button>
    `,
  ).join("");
}

function generateLanguagePickerHtml(): string {
  const selectedLanguage =
    HOME_LANGUAGES.find((language) => language.id === activeHomeLanguage) ??
    HOME_LANGUAGES[0];

  return `
    <div class="home-language-control" id="home-language-control">
      <button
        class="home-language-btn"
        id="home-language-btn"
        type="button"
        aria-haspopup="dialog"
        aria-expanded="false"
        aria-controls="home-language-popup"
      >
        <span class="home-language-globe" aria-hidden="true">🌐</span>
        <span id="home-language-button-label">${selectedLanguage.name}</span>
        <span class="home-language-chevron" aria-hidden="true">▾</span>
      </button>

      <div class="home-language-backdrop hidden" id="home-language-backdrop"></div>

      <div
        class="home-language-popup hidden"
        id="home-language-popup"
        role="dialog"
        aria-modal="false"
        aria-labelledby="home-language-popup-title"
      >
        <div class="home-language-popup-header">
          <span id="home-language-popup-title">${escapeHtml(
            textFor(UI_TEXT.chooseLanguage),
          )}</span>
          <button
            class="home-language-close"
            id="home-language-close"
            type="button"
            aria-label="${escapeHtml(textFor(UI_TEXT.closeLanguage))}"
          >
            ×
          </button>
        </div>

        <div class="home-language-options">
          ${HOME_LANGUAGES.map(
            (language) => `
              <button
                class="home-language-option ${
                  language.id === activeHomeLanguage ? "active" : ""
                }"
                type="button"
                data-language="${language.id}"
                aria-pressed="${
                  language.id === activeHomeLanguage ? "true" : "false"
                }"
              >
                <span>${language.name}</span>
                <span class="home-language-check" aria-hidden="true">
                  ${language.id === activeHomeLanguage ? "✓" : ""}
                </span>
              </button>
            `,
          ).join("")}
        </div>
      </div>
    </div>
  `;
}

function generateNewsCardsHtml(): string {
  if (homeNewsLoading) {
    return `<div class="home-news-status" role="status">Loading real stories…</div>`;
  }

  if (homeNewsError) {
    return `
      <div class="home-news-status home-news-error" role="alert">
        <span>Stories could not be loaded.</span>
        <button id="home-news-retry" type="button">Try again</button>
      </div>
    `;
  }

  if (homeNewsArticles.length === 0) {
    return `<div class="home-news-status">No current stories were found for this topic.</div>`;
  }

  const featured = homeNewsArticles.find((article) => article.is_featured) ?? homeNewsArticles[0];
  const cards = homeNewsArticles.filter((article) => article.id !== featured.id).slice(0, 6);
  const featuredTitle = escapeHtml(featured.title);
  const featuredSummary = escapeHtml(featured.summary);

  return `
    <div class="home-news-layout" data-feed-request-id="${escapeHtml(homeFeedRequestId)}">
      <a class="home-featured-story" href="${escapeHtml(featured.article_url)}" target="_blank" rel="noopener noreferrer">
        <div class="home-featured-image-wrap">
          <img class="home-featured-image" src="${escapeHtml(articleImage(featured))}" alt="${featuredTitle}" loading="eager" style="object-position:${imageObjectPosition(featured)}" onerror="this.onerror=null;this.src='${FALLBACK_NEWS_IMAGE}'" />
          <span class="home-featured-label">${escapeHtml(textFor(UI_TEXT.topStory))}</span>
        </div>
        <div class="home-featured-content">
          <div class="home-story-source-row">
            <span class="home-story-source">${escapeHtml(featured.source.name)}</span>
            <span class="home-story-time">${escapeHtml(relativePublishedTime(featured.published_at))}</span>
          </div>
          <h3>${featuredTitle}</h3>
          <p>${featuredSummary}</p>
        </div>
      </a>
      <div class="home-news-card-grid">
        ${cards.map((article) => `
          <a class="home-news-card" href="${escapeHtml(article.article_url)}" target="_blank" rel="noopener noreferrer">
            <img class="home-news-card-image" src="${escapeHtml(articleImage(article))}" alt="${escapeHtml(article.title)}" loading="lazy" style="object-position:${imageObjectPosition(article)}" onerror="this.onerror=null;this.src='${FALLBACK_NEWS_IMAGE}'" />
            <div class="home-news-card-body">
              <div class="home-story-source-row">
                <span class="home-story-source">${escapeHtml(article.source.name)}</span>
                <span class="home-story-time">${escapeHtml(relativePublishedTime(article.published_at))}</span>
              </div>
              <h3>${escapeHtml(article.title)}</h3>
              <p>${escapeHtml(article.summary)}</p>
            </div>
          </a>
        `).join("")}
      </div>
    </div>
  `;
}

function generateNewsFeedHtml(): string {
  return `
    <section class="home-discover-section" aria-labelledby="home-news-heading">
      <div class="home-discover-header">
        <div>
          <span class="home-discover-eyebrow">${escapeHtml(
            textFor(UI_TEXT.discover),
          )}</span>
          <h2 id="home-news-heading">${escapeHtml(
            textFor(UI_TEXT.newsForYou),
          )}</h2>
        </div>

      </div>

      <div class="home-topic-scroller" aria-label="News topics">
        <div class="home-topic-row" id="home-topic-row">
          ${generateTopicTabsHtml()}
        </div>
      </div>

      <div id="home-news-results">
        ${generateNewsCardsHtml()}
      </div>
    </section>
  `;
}

export function renderHome() {
  const app = document.getElementById("app");
  if (!app) return;

  const {theme, applyTheme} = useTheme();
  const {user} = useAuth();

  applyTheme(theme);

  const isLoggedIn = Boolean(user?.email);
  const profileLetter = isLoggedIn ? user!.email[0].toUpperCase() : "";
  const profileColor = isLoggedIn ? getProfileColor(user!.email) : "";

  app.innerHTML = `
    <div class="home-wrapper">
      <nav class="top-nav desktop-nav">
        <div class="nav-left">
          <button>${escapeHtml(textFor(UI_TEXT.about))}</button>
          <button>${escapeHtml(textFor(UI_TEXT.store))}</button>
        </div>

        <div class="nav-right">
          <button class="theme-toggle-btn" id="theme-toggle">
            ${escapeHtml(textFor(UI_TEXT.theme))}: ${escapeHtml(
              getThemeLabel(theme),
            )}
          </button>
          <button>AMailMe</button>
          <button>${escapeHtml(textFor(UI_TEXT.images))}</button>

          <div class="apps-dropdown-container">
            <button class="apps-btn" id="apps-btn" type="button" aria-label="${escapeHtml(
              textFor(UI_TEXT.openApps),
            )}">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <circle cx="5" cy="5" r="2.5"/><circle cx="12" cy="5" r="2.5"/>
                <circle cx="19" cy="5" r="2.5"/><circle cx="5" cy="12" r="2.5"/>
                <circle cx="12" cy="12" r="2.5"/><circle cx="19" cy="12" r="2.5"/>
                <circle cx="5" cy="19" r="2.5"/><circle cx="12" cy="19" r="2.5"/>
                <circle cx="19" cy="19" r="2.5"/>
              </svg>
            </button>
            ${generateAppsMenuHtml()}
          </div>

          <div class="profile-container" id="desktop-profile">
            ${
              isLoggedIn
                ? `<div class="profile-icon" style="background-color:${profileColor}" id="profile-icon">${profileLetter}</div>`
                : `<button class="signin-btn" id="signin-btn">${escapeHtml(
                    textFor(UI_TEXT.signIn),
                  )}</button>`
            }
          </div>
        </div>
      </nav>

      <nav class="mobile-nav">
        <div class="mobile-left">
          <button class="hamburger-btn" type="button" aria-label="${escapeHtml(
            textFor(UI_TEXT.openNavigation),
          )}">☰</button>
        </div>

        <div class="mobile-center">
          <button class="mobile-nav-btn active" type="button">${escapeHtml(
            textFor(UI_TEXT.all),
          )}</button>
          <button class="mobile-nav-btn" type="button">${escapeHtml(
            textFor(UI_TEXT.images),
          )}</button>
        </div>

        <div class="mobile-right">
          <div class="apps-dropdown-container">
            <button class="apps-btn" id="mobile-apps-btn" type="button" aria-label="${escapeHtml(
              textFor(UI_TEXT.openApps),
            )}">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <circle cx="5" cy="5" r="2.5"/><circle cx="12" cy="5" r="2.5"/>
                <circle cx="19" cy="5" r="2.5"/><circle cx="5" cy="12" r="2.5"/>
                <circle cx="12" cy="12" r="2.5"/><circle cx="19" cy="12" r="2.5"/>
                <circle cx="5" cy="19" r="2.5"/><circle cx="12" cy="19" r="2.5"/>
                <circle cx="19" cy="19" r="2.5"/>
              </svg>
            </button>
          </div>

          <div class="profile-container" id="mobile-profile">
            ${
              isLoggedIn
                ? `<div class="profile-icon mobile-profile" style="background-color:${profileColor}" id="mobile-profile-icon">${profileLetter}</div>`
                : `<button class="signin-btn mobile-signin" id="mobile-signin-btn">${escapeHtml(
                    textFor(UI_TEXT.signIn),
                  )}</button>`
            }
          </div>
        </div>
      </nav>

      <main class="home-main">
        <h1 class="home-logo">AyeSearch</h1>

        <div class="home-search-wrapper" id="search-wrapper">
          <div class="search-bar">
            <span class="search-icon" aria-hidden="true">🔍</span>
            <input
              id="search-input"
              type="text"
              placeholder="${escapeHtml(textFor(UI_TEXT.searchPlaceholder))}"
              autocomplete="off"
              aria-label="${escapeHtml(textFor(UI_TEXT.searchPlaceholder))}"
            />
            <span class="mic-icon" aria-hidden="true">🎙️</span>
          </div>
          <div id="suggestions-container"></div>
        </div>

        <button class="ai-zone-btn" id="ai-zone-btn">AI Zone</button>

        <div class="action-buttons">
          <button id="search-btn">${escapeHtml(
            textFor(UI_TEXT.searchButton),
          )}</button>
          ${generateLanguagePickerHtml()}
        </div>

        ${generateNewsFeedHtml()}
      </main>

      <footer class="footer">
        <div class="footer-left">
          <button>Advertising</button>
          <button>Business</button>
        </div>

        <div class="footer-center">
          <span>Applying AI towards a future that is for us</span>
        </div>

        <div class="footer-right">
          <button>Privacy</button>
          <button>Terms</button>
          <button class="mobile-theme-btn" id="mobile-theme-btn">
            ${escapeHtml(textFor(UI_TEXT.theme))}: ${escapeHtml(
              getThemeLabel(theme),
            )}
          </button>
        </div>
      </footer>
    </div>
  `;

  setupHomeEvents();
}

function setupLanguageEvents(): void {
  const languageControl = document.getElementById("home-language-control");
  const languageButton = document.getElementById("home-language-btn");
  const languagePopup = document.getElementById("home-language-popup");
  const languageBackdrop = document.getElementById("home-language-backdrop");
  const languageClose = document.getElementById("home-language-close");

  const closeLanguagePicker = () => {
    languagePopup?.classList.add("hidden");
    languageBackdrop?.classList.add("hidden");
    languageButton?.setAttribute("aria-expanded", "false");
    document.body.classList.remove("home-language-modal-open");
  };

  const openLanguagePicker = () => {
    languagePopup?.classList.remove("hidden");
    languageBackdrop?.classList.remove("hidden");
    languageButton?.setAttribute("aria-expanded", "true");

    if (window.matchMedia("(max-width: 768px)").matches) {
      document.body.classList.add("home-language-modal-open");
    }
  };

  languageButton?.addEventListener("click", (event) => {
    event.stopPropagation();

    if (languagePopup?.classList.contains("hidden")) {
      openLanguagePicker();
    } else {
      closeLanguagePicker();
    }
  });

  languageClose?.addEventListener("click", closeLanguagePicker);
  languageBackdrop?.addEventListener("click", closeLanguagePicker);

  languagePopup
    ?.querySelectorAll<HTMLButtonElement>(".home-language-option")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const language = button.dataset.language as HomeLanguage | undefined;

        if (!language || language === activeHomeLanguage) {
          closeLanguagePicker();
          return;
        }

        saveHomeLanguage(language);
        closeLanguagePicker();
        renderHome();
      });
    });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeLanguagePicker();
    }
  });

  document.addEventListener("click", (event) => {
    const target = event.target as Node;

    if (
      languageControl &&
      !languageControl.contains(target) &&
      !languagePopup?.classList.contains("hidden")
    ) {
      closeLanguagePicker();
    }
  });
}

function setupTopicEvents(): void {
  const topicButtons = document.querySelectorAll<HTMLButtonElement>(".home-topic-chip");

  topicButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const selectedTopic = button.dataset.topic ?? "top";
      if (selectedTopic === "wiki") {
        window.location.assign("https://aikipedia.ayesearch.com/");
        return;
      }

      activeHomeTopic = selectedTopic;
      topicButtons.forEach((topicButton) => {
        const isActive = topicButton === button;
        topicButton.classList.toggle("active", isActive);
        topicButton.setAttribute("aria-pressed", String(isActive));
      });

      void loadDiscoverFeed(selectedTopic);
    });
  });

  document.getElementById("home-news-results")?.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.closest("#home-news-retry")) void loadDiscoverFeed(activeHomeTopic);
  });
}

function setupHomeEvents() {
  const {toggleTheme} = useTheme();

  const handleThemeToggle = () => {
    toggleTheme();
    window.setTimeout(renderHome, 50);
  };

  document
    .getElementById("theme-toggle")
    ?.addEventListener("click", handleThemeToggle);

  document
    .getElementById("mobile-theme-btn")
    ?.addEventListener("click", handleThemeToggle);

  const handleLogin = () => {
    const next = encodeURIComponent(window.location.href);

    window.location.assign(
      `https://api.ayesearch.com/auth/ayesearch/login/?next=${next}`,
    );
  };

  document.getElementById("signin-btn")?.addEventListener("click", handleLogin);

  document
    .getElementById("mobile-signin-btn")
    ?.addEventListener("click", handleLogin);

  const desktopIcon = document.getElementById("profile-icon");
  const mobileIcon = document.getElementById("mobile-profile-icon");

  const openProfile = (element: HTMLElement) => {
    renderProfileDropdown(element, () => {});
  };

  desktopIcon?.addEventListener("click", (event) => {
    event.stopPropagation();
    openProfile(desktopIcon);
  });

  mobileIcon?.addEventListener("click", (event) => {
    event.stopPropagation();
    openProfile(mobileIcon);
  });

  const appsBtn = document.getElementById("apps-btn");
  const mobileAppsBtn = document.getElementById("mobile-apps-btn");
  const appsPanel = document.getElementById("apps-dropdown-panel");
  const searchInput = document.getElementById(
    "search-input",
  ) as HTMLInputElement | null;
  const suggestionsContainer = document.getElementById(
    "suggestions-container",
  );

  const toggleAppsPanel = (event: Event) => {
    event.stopPropagation();

    if (!appsPanel || !suggestionsContainer) return;

    const isHidden = appsPanel.classList.contains("hidden");

    if (isHidden) {
      suggestionsContainer.innerHTML = "";
      appsPanel.classList.remove("hidden");

      if (mobileAppsBtn && event.currentTarget === mobileAppsBtn) {
        mobileAppsBtn.parentElement?.appendChild(appsPanel);
      } else if (appsBtn && event.currentTarget === appsBtn) {
        appsBtn.parentElement?.appendChild(appsPanel);
      }
    } else {
      appsPanel.classList.add("hidden");
    }
  };

  appsBtn?.addEventListener("click", toggleAppsPanel);
  mobileAppsBtn?.addEventListener("click", toggleAppsPanel);

  document.addEventListener("click", (event) => {
    const target = event.target as Node;

    if (
      appsPanel &&
      !appsPanel.contains(target) &&
      !appsBtn?.contains(target) &&
      !mobileAppsBtn?.contains(target)
    ) {
      appsPanel.classList.add("hidden");
    }

    const searchWrapper = document.getElementById("search-wrapper");

    if (
      suggestionsContainer &&
      searchWrapper &&
      !searchWrapper.contains(target)
    ) {
      suggestionsContainer.innerHTML = "";
    }
  });

  let searchTimeout: number | undefined;

  searchInput?.addEventListener("input", () => {
    if (searchTimeout !== undefined) {
      window.clearTimeout(searchTimeout);
    }

    searchTimeout = window.setTimeout(() => {
      appsPanel?.classList.add("hidden");

      if (!suggestionsContainer) return;

      renderHomeSearchSuggestions(
        searchInput.value,
        (value) => {
          searchInput.value = value;
          handleSearch(value);
        },
        suggestionsContainer,
      );
    }, 80);
  });

  searchInput?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      handleSearch(searchInput.value);
    }
  });

  document.getElementById("search-btn")?.addEventListener("click", () => {
    handleSearch(searchInput?.value ?? "");
  });


  document.getElementById("ai-zone-btn")?.addEventListener("click", () => {
    const query = searchInput?.value.trim() ?? "";
    window.location.href = query
      ? `/aizone?q=${encodeURIComponent(query)}`
      : "/aizone";
  });

  setupTopicEvents();
  setupLanguageEvents();
  void loadDiscoverFeed(activeHomeTopic);

}

function handleSearch(query: string) {
  const trimmed = query.trim();
  if (!trimmed) return;

  const normalized = normalizeQuery(trimmed);

  if (searchMap[normalized]) {
    window.location.href = searchMap[normalized];
    return;
  }

  window.location.href = `/search?q=${encodeURIComponent(trimmed)}`;
}
